import { useKestrel, useKestrelViewerContext } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import {
  calculateDistance,
  parsePosition,
} from 'features/kestrelViewer/utils/measurementUtils'
import {
  convertFromCentimeters,
  getUnitLabel,
} from 'features/kestrelViewer/utils/unitConversionUtils'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { styled } from 'styled-components'
import { KVMeasurement, KVMeasurementSegment } from 'types'

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledInput = styled.input<{ $isFocused: boolean }>`
  font-size: 12px;
  padding: 2px 8px;
  width: 80px;
  height: 26px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.3); // Match label input border
  background: ${(props) =>
    props.$isFocused
      ? 'rgba(255, 255, 255, 0.1)'
      : 'transparent'}; // Match label input background behavior
  color: white;
  outline: none;
  transition: background-color 0.2s ease;

  &:focus {
    border-color: rgba(255, 255, 255, 0.6); // Match label input focus border
  }
`

const UnitLabel = styled.span`
  font-size: 14px;
  color: white;
  opacity: 0.8;
`

export const MeasurementSegmentLengthInput: FC<{
  selectedMeasurement: KVMeasurement
  selectedMeasurementSegment: KVMeasurementSegment
}> = ({ selectedMeasurement, selectedMeasurementSegment }) => {
  const { sendCommand, currentUnit } = useKestrelViewerContext()
  const { kestrelModel } = useKestrel()
  const [inputValue, setInputValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const lastSegmentIdRef = React.useRef<string>('')

  // Get measurement scale from Kestrel model
  const measurementScale = kestrelModel?.data.measurementScale ?? 1

  // Calculate the measurement value from segment positions
  const calculatedLength = useMemo(() => {
    const startPos = parsePosition(
      selectedMeasurementSegment.measurementSegmentStartPosition
    )
    const endPos = parsePosition(
      selectedMeasurementSegment.measurementSegmentEndPosition
    )

    if (startPos && endPos) {
      const distanceInUnrealUnits = calculateDistance(startPos, endPos)
      const distanceInCm = distanceInUnrealUnits * measurementScale
      // Convert from cm to the target unit (same logic as formatPerimeter)
      const convertedValue = convertFromCentimeters(distanceInCm, currentUnit)
      return convertedValue
    }
    return null
  }, [
    selectedMeasurementSegment.measurementSegmentStartPosition,
    selectedMeasurementSegment.measurementSegmentEndPosition,
    currentUnit,
    measurementScale,
  ])

  // Update input value when segment changes or when unit changes
  useEffect(() => {
    const segmentChanged =
      lastSegmentIdRef.current !==
      selectedMeasurementSegment.measurementSegmentId

    if (segmentChanged) {
      lastSegmentIdRef.current = selectedMeasurementSegment.measurementSegmentId
    }

    // Always update the input value when calculatedLength or unit changes
    if (calculatedLength !== null) {
      setInputValue(calculatedLength.toFixed(3))
    } else {
      setInputValue('')
    }
  }, [selectedMeasurementSegment.measurementSegmentId, calculatedLength])

  const handleSubmit = () => {
    if (inputValue === '') {
      return
    }

    if (!/^\d*\.?\d*$/.test(inputValue)) {
      return
    }

    const numValue = parseFloat(inputValue)
    if (numValue <= 0) {
      return
    }

    sendCommand({
      command: KestrelCommands.UPDATE_MEASUREMENT,
      measurementId: selectedMeasurement.id,
      measurementSegmentId: selectedMeasurementSegment.measurementSegmentId,
      measurementSegmentValue: numValue,
    })
  }

  return (
    <InputContainer>
      <StyledInput
        type='text'
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false)
          handleSubmit()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.stopPropagation()
            handleSubmit()
          }
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        $isFocused={isFocused}
      />
      <UnitLabel>{getUnitLabel(currentUnit)}</UnitLabel>
    </InputContainer>
  )
}
